import type { TSiteSectionsTreeData } from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.types";
import type { SiteSectionId } from "@/data/common/ids";
import type { IVirtualizedTreeNode } from "@voltalis/ui-kit/components";

type TreeNode = IVirtualizedTreeNode<TSiteSectionsTreeData>;

export interface TreeBulkSectionSelectionState {
  isSelected: boolean;
  isIndeterminate: boolean;
}

export function getTreeBulkSectionSelectionState(
  section: TreeNode,
  selectedSectionIds: ReadonlySet<SiteSectionId>,
): TreeBulkSectionSelectionState {
  const sectionIds = getTreeSectionIds(section);
  const selectedCount = sectionIds.filter((sectionId) => selectedSectionIds.has(sectionId)).length;

  return {
    isSelected: sectionIds.length > 0 && selectedCount === sectionIds.length,
    isIndeterminate: selectedCount > 0 && selectedCount < sectionIds.length,
  };
}

export function toggleTreeBulkSectionSelection(
  selectedSectionIds: ReadonlySet<SiteSectionId>,
  section: TreeNode,
  isSelected: boolean,
): Set<SiteSectionId> {
  const nextSelectedIds = new Set(selectedSectionIds);
  const sectionIds = getTreeSectionIds(section);

  sectionIds.forEach((sectionId) => {
    if (isSelected) {
      nextSelectedIds.add(sectionId);
    } else {
      nextSelectedIds.delete(sectionId);
    }
  });

  return nextSelectedIds;
}

export function getTreeDescendantSectionIds(section: TreeNode): SiteSectionId[] {
  return (section.children ?? []).flatMap(getTreeSectionIds);
}

export function getTreeSectionAndDescendantSectionIds(section: TreeNode): SiteSectionId[] {
  return getTreeSectionIds(section);
}

function getTreeSectionIds(section: TreeNode): SiteSectionId[] {
  const sectionIds: SiteSectionId[] = [];

  if (section.data) {
    sectionIds.push(section.data.id);
  }

  (section.children ?? []).forEach((child) => {
    sectionIds.push(...getTreeSectionIds(child));
  });

  return sectionIds;
}
